import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  FlightTakeoff as FlightIcon,
  Public as DestinoIcon,
  Warning as DGIcon,
  People as ProductividadIcon,
  Today as TodayIcon,
  CalendarMonth as MonthIcon,
  LocalShipping as PalletIcon,
  Scale as WeightIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';

const reportTypes = [
  {
    title: 'Reporte de Vuelos',
    description: 'Historial de vuelos con estadísticas de carga',
    icon: <FlightIcon sx={{ fontSize: 48 }} />,
    path: '/reportes/vuelos',
    color: '#1565C0',
  },
  {
    title: 'Carga por Destino',
    description: 'Análisis de carga enviada a cada destino',
    icon: <DestinoIcon sx={{ fontSize: 48 }} />,
    path: '/reportes/destinos',
    color: '#2E7D32',
  },
  {
    title: 'Dangerous Goods',
    description: 'Registro de mercancía peligrosa transportada',
    icon: <DGIcon sx={{ fontSize: 48 }} />,
    path: '/reportes/dangerous-goods',
    color: '#F57C00',
  },
  {
    title: 'Productividad',
    description: 'Rendimiento de paletizadores y distribuidores',
    icon: <ProductividadIcon sx={{ fontSize: 48 }} />,
    path: '/reportes/productividad',
    color: '#7B1FA2',
  },
];

const ReportesIndex = () => {
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResumen();
  }, []);

  const fetchResumen = async () => {
    try {
      const response = await api.get(endpoints.reportes.resumen);
      setResumen(response.data.data);
    } catch (error) {
      console.error('Error al cargar resumen:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon, value, label, sublabel, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}15`,
              color: color,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            {sublabel && (
              <Typography variant="caption" color="text.disabled">
                {sublabel}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Reportes y Estadísticas
        </Typography>
        <Typography color="text.secondary">
          Panel de control con métricas y generación de reportes
        </Typography>
      </Box>

      {/* Stats del día y mes */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : resumen && (
        <>
          {/* Stats Cards */}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TodayIcon /> Estadísticas de Hoy
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} md={4}>
              <StatCard
                icon={<FlightIcon />}
                value={resumen.hoy.vuelos}
                label="Vuelos"
                color="#1565C0"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <StatCard
                icon={<PalletIcon />}
                value={resumen.hoy.pallets}
                label="Pallets"
                color="#2E7D32"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<WeightIcon />}
                value={`${resumen.hoy.peso_kg.toLocaleString()} kg`}
                label="Peso Total"
                sublabel={`${Math.round(resumen.hoy.peso_kg * 2.20462).toLocaleString()} lbs`}
                color="#7B1FA2"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonthIcon /> Estadísticas del Mes
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<FlightIcon />}
                value={resumen.mes.vuelos}
                label="Vuelos"
                color="#1565C0"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<PalletIcon />}
                value={resumen.mes.pallets}
                label="Pallets"
                color="#2E7D32"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<WeightIcon />}
                value={`${Math.round(resumen.mes.peso_kg / 1000)}t`}
                label="Peso Total"
                sublabel={`${resumen.mes.peso_kg.toLocaleString()} kg`}
                color="#7B1FA2"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<DGIcon />}
                value={resumen.mes.dg_count}
                label="DG Items"
                color="#F57C00"
              />
            </Grid>
          </Grid>

          {/* Vuelos Abiertos y Top Destinos */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Card>
                <Box sx={{ p: 2, bgcolor: 'warning.main', color: 'white' }}>
                  <Typography variant="h6" fontWeight="bold">
                    Vuelos Abiertos ({resumen.vuelos_abiertos.length})
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>AWB</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Destino</TableCell>
                        <TableCell align="center">Pallets</TableCell>
                        <TableCell align="right">Peso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resumen.vuelos_abiertos.length > 0 ? (
                        resumen.vuelos_abiertos.map((v) => (
                          <TableRow key={v.awb} hover>
                            <TableCell>
                              <Typography
                                component={Link}
                                to={`/vuelos/${v.awb}/gestionar`}
                                sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}
                              >
                                {v.awb}
                              </Typography>
                            </TableCell>
                            <TableCell>{new Date(v.fecha).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Chip label={v.destino} size="small" />
                            </TableCell>
                            <TableCell align="center">{v.pallets}</TableCell>
                            <TableCell align="right">{(v.peso || 0).toLocaleString()} kg</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No hay vuelos abiertos</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <Box sx={{ p: 2, bgcolor: 'success.main', color: 'white' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingIcon /> Top Destinos del Mes
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Destino</TableCell>
                        <TableCell align="center">Vuelos</TableCell>
                        <TableCell align="right">Peso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resumen.top_destinos.map((d, idx) => (
                        <TableRow key={d.destino} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: idx === 0 ? 'warning.main' : idx === 1 ? 'grey.400' : idx === 2 ? 'warning.dark' : 'grey.200',
                                  color: idx < 3 ? 'white' : 'text.primary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                {idx + 1}
                              </Typography>
                              <Typography fontWeight={idx < 3 ? 'bold' : 'normal'}>
                                {d.destino}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{d.vuelos}</TableCell>
                          <TableCell align="right">{(d.peso || 0).toLocaleString()} kg</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Report Types */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Generar Reportes
      </Typography>
      <Grid container spacing={3}>
        {reportTypes.map((report) => (
          <Grid item xs={12} sm={6} md={3} key={report.path}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardActionArea
                component={Link}
                to={report.path}
                sx={{ height: '100%', p: 3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: `${report.color}15`,
                      color: report.color,
                    }}
                  >
                    {report.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {report.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {report.description}
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ReportesIndex;

